// steps/Step2FetchConsumerData.tsx
import {
  useDashboardSummary,
  useUpdateBillingConfiguration,
  useDraftDetails,
} from "@features/bx/BulkBillGeneration/hooks";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { StepHelpers } from "@shared/components/Stepper";
import { Button } from "@shared/ui/button";
import { Checkbox } from "@shared/ui/checkbox";
import { Label } from "@shared/ui/label";
import { Tabs } from "@shared/ui/tabs";
import {
  CreditCard,
  DollarSign,
  FileText,
  TrendingUp,
  Users,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ConsumerTab from "./Tabs/ConsumerTab";
import PaymentDetailsTab from "./Tabs/PaymentDetails";
import PlansAndTariffTab from "./Tabs/PlansTariff/PlansTariff";
import { toast } from "@shared/hooks/use-toast";

interface Step2FetchConsumerDataProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

const Step2FetchConsumerData: React.FC<Step2FetchConsumerDataProps> = ({
  stepHelpers,
  currentStepIndex = 1,
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  setValidationError,
  clearValidationError,
}) => {
  const navigate = useNavigate();
  const { id, cycleId, action } = useParams();

  const [consumerDataReviewed, setConsumerDataReviewed] = useState(
    action === "edit" || data?.consumerDataReviewed || false,
  );

  const { mutateAsync: updateBillingCycle, isPending: isUpdating } =
    useUpdateBillingConfiguration(id);
  const [plansDataReviewed, setPlansDataReviewed] = useState(
    action === "edit" || data?.plansDataReviewed || false,
  );

  const [includeFirstBillCharges, setIncludeFirstBillCharges] = useState(true);

  const [serviceChargeStates, setServiceChargeStates] = useState<
    Record<string, string[]>
  >({});

  // Toggle states
  const [applyCredits, setApplyCredits] = useState(true);
  const [includeLateFees, setIncludeLateFees] = useState(true);
  const [carryforwardOutstanding, setCarryforwardOutstanding] = useState(true);
  const [includeInstallments, setIncludeInstallments] = useState(true);

  const { data: draftdetails } = useDraftDetails(id || "");
  const initializedFromDraft = React.useRef(false);

  useEffect(() => {
    if (draftdetails && !initializedFromDraft.current) {
       const step2Config = draftdetails?.billDraftData?.step_2Config;
       
       if (step2Config?.includeServiceCharges) {
         setServiceChargeStates(step2Config.includeServiceCharges);
       }
       
       if (step2Config) {
         if (step2Config.consumerDataReviewed !== undefined) setConsumerDataReviewed(step2Config.consumerDataReviewed);
         if (step2Config.plansDataReviewed !== undefined) setPlansDataReviewed(step2Config.plansDataReviewed);
         if (step2Config.includeFirstBillCharges !== undefined) setIncludeFirstBillCharges(step2Config.includeFirstBillCharges);
         if (step2Config.applyCredits !== undefined) setApplyCredits(step2Config.applyCredits);
         if (step2Config.includeLateFees !== undefined) setIncludeLateFees(step2Config.include_late_fees);
         if (step2Config.carryforwardOutstandings !== undefined) setCarryforwardOutstanding(step2Config.carryforward_outstandings);
         if (step2Config.includeInstallments !== undefined) setIncludeInstallments(step2Config.include_installments);
       }
       initializedFromDraft.current = true;
    }
  }, [draftdetails]);

  const enabledServiceCharges = Object.values(serviceChargeStates).flat();

  const handleServiceChargeChange = (value: Record<string, string[]>) => {
    setServiceChargeStates(value);
  };
  // Handle checkbox changes
  const handleConsentChange = useCallback(
    (field: string, value: boolean) => {
      if (field === "consumerDataReviewed") {
        setConsumerDataReviewed(value);
      } else if (field === "plansDataReviewed") {
        setPlansDataReviewed(value);
      }

      const updatedData = {
        ...data,
        [field]: value,
      };

      onDataChange?.(updatedData);

      if (stepHelpers) {
        stepHelpers.setStepData(currentStepIndex, updatedData);
      }
    },
    [data, onDataChange, stepHelpers, currentStepIndex],
  );

  // Handle Next
  const handleNext = async (isDraft: boolean) => {
    // Validation: Both checkboxes must be checked
    if (!consumerDataReviewed || !plansDataReviewed) {
      setValidationError?.(
        "Please review and confirm both consumer data and plans data before proceeding.",
      );
       toast({
              title: "",
              description: "Please review and confirm both consumer data and plans data before proceeding.",
              variant: "destructive",
            });
      return;
    }

    clearValidationError?.();

    clearValidationError?.();

    try {
      const payload = {
        cycle_id: cycleId,
        step_2_config: {
          consumer_data_reviewed: consumerDataReviewed,
          plans_tariff_reviewed: plansDataReviewed,
          include_first_bill_charges: includeFirstBillCharges,
          apply_credits: applyCredits,
          include_late_fees: includeLateFees,
          carryforward_outstandings: carryforwardOutstanding,
          include_installments: includeInstallments,
          include_service_charges: serviceChargeStates,
        },
        step: {
          step_working_on: 1,
          total_steps: 5,
        },
      };

      const response = await updateBillingCycle(payload);

      if (response) {
        const completedData = {
          ...data,
          consumerDataReviewed,
          plansDataReviewed,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        };

        onDataChange?.(completedData);
        if (isDraft) {
          navigate("/bx/create-bill-generation");
        } else {
          onNext?.();
        }
      } else {
        setValidationError?.("Failed to update asset data. Please try again.");
      }
    } catch (error) {
      console.error("Failed to continue from step 3:", error);
      setValidationError?.(
        "An error occurred while processing. Please try again.",
      );
    }
  };

  const { data: consumerData } = useDashboardSummary(cycleId);

  // KPI Cards Data
  const kpiCards = [
    {
      title: "Total Active Consumers",
      count: consumerData?.totalActiveConsumers?.count,
      icon: <Users className="h-6 w-6 text-blue-500" />,
    },
    {
      title: "Active Plans",
      count: consumerData?.activePlans?.count,
      icon: <TrendingUp className="h-6 w-6 text-green-500" />,
    },
    {
      title: "Total Outstanding",
      count: consumerData?.totalOutstanding?.amount,
      icon: <DollarSign className="h-6 w-6 text-orange-500" />,
    },
  ];

  // Tab configuration
  const tabComponents = {
    consumer: {
      label: "Consumer",
      icon: <Users className="h-4 w-4" />,
      component: (
        <ConsumerTab
          data={data}
          includeFirstBillCharges={includeFirstBillCharges}
          setIncludeFirstBillCharges={setIncludeFirstBillCharges}
        />
      ),
    },
    plans: {
      label: "Rates & Plans",
      icon: <FileText className="h-4 w-4" />,
      component: (
        <PlansAndTariffTab
          serviceChargeStates={serviceChargeStates}
          onServiceChargeChange={handleServiceChargeChange}
        />
      ),
    },
    payment: {
      label: "Payment Details",
      icon: <CreditCard className="h-4 w-4" />,
      component: (
        <PaymentDetailsTab
          applyCredits={applyCredits}
          includeLateFees={includeLateFees}
          carryforwardOutstanding={carryforwardOutstanding}
          includeInstallments={includeInstallments}
          setApplyCredits={setApplyCredits}
          setIncludeLateFees={setIncludeLateFees}
          setCarryforwardOutstanding={setCarryforwardOutstanding}
          setIncludeInstallments={setIncludeInstallments}
        />
      ),
    },
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {kpiCards.map((card, index) => (
          <QuickStatsCard key={index} count={card.count} title={card.title} />
        ))}
      </div>
      {/* Tabs */}
      <Tabs
        tabComponents={tabComponents}
        defaultValue="consumer"
        urlMapping={{
          consumer: "consumer",
          plans: "plans",
          payment: "payment",
        }}
        tabsListClassName="w-full grid grid-cols-3 bg-muted/30 p-1"
        idPrefix="fetch-consumer-tab"
        disableUrlSync={true}
      />

      {/* Review & Consent - OUTSIDE TABS */}
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
        <h3 className="font-semibold mb-2">Review & Consent</h3>
        <p className="text-sm text-gray-600 mb-4">
          Please review and confirm the following before proceeding to the next
          step.
        </p>

        <div className="space-y-3">
          <div className="flex items-start gap-3 bg-white rounded-lg p-4 border">
            <Checkbox
              id="consumer-data-reviewed"
              checked={consumerDataReviewed}
              onCheckedChange={(checked) =>
                handleConsentChange("consumerDataReviewed", checked as boolean)
              }
            />
            <div className="flex-1">
              <Label
                htmlFor="consumer-data-reviewed"
                className="font-medium cursor-pointer"
              >
                Consumer data reviewed
              </Label>
              <p className="text-sm text-gray-600">
                I confirm that all consumer information, account details, and
                service configurations have been reviewed and are accurate.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 bg-white rounded-lg p-4 border">
            <Checkbox
              id="plans-data-reviewed"
              checked={plansDataReviewed}
              onCheckedChange={(checked) =>
                handleConsentChange("plansDataReviewed", checked as boolean)
              }
            />
            <div className="flex-1">
              <Label
                htmlFor="plans-data-reviewed"
                className="font-medium cursor-pointer"
              >
                Plans and tariff data review
              </Label>
              <p className="text-sm text-gray-600">
                I confirm that all active plans, tariff structures, and pricing
                configurations have been reviewed and are correct for this
                billing cycle.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4 border-t">
        <Button onClick={onPrevious} variant="outline">
          Previous
        </Button>
        <div className="flex gap-4">
          <Button onClick={() => handleNext(true)}>Save As Draft</Button>
          <Button onClick={() => handleNext(false)}>Next</Button>
        </div>
      </div>
    </div>
  );
};

export default Step2FetchConsumerData;
